import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { fetchTendersV2 } from '../../api/v2/tenders';
import type { TenderListItemV2, TenderStatusV2 } from '../../types';
import StatusBadge from '../../components/StatusBadge';

/**
 * v2 tender list.
 *
 * Reads the v2 endpoint (GET /api/v2/tenders, backed by the `tenders` table via
 * TenderV2). That DTO is leaner than the v1 `Tender`: it carries title, status
 * and the received date only — so the location/sector/skills columns and
 * free-text search from the v1 list are not shown here. Columns are sortable
 * (server-side), defaulting to newest received first. Tender links route to
 * /v2/tenders/:id, and the per-row Embed / Run match actions are dropped.
 */
export default function TenderList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [tenders, setTenders] = useState<TenderListItemV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TenderStatusV2 | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Sort state — maps to the backend's Spring `sort` param (TenderV2 property
  // names). Default: newest received first.
  const [sortField, setSortField] = useState<SortField>('receivedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  // Toggle direction when re-clicking the active column; otherwise switch to the
  // new column ascending. Resets to the first page since the ordering changed.
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  // v2 lifecycle statuses (TenderV2). Labels are plain text — the v2 enums have
  // no i18n keys yet; only the "all" option is translated.
  const STATUS_OPTIONS: Array<{ value: TenderStatusV2 | ''; label: string }> = [
    { value: '',                    label: t('tenders.allStatuses') },
    { value: 'NEW',                 label: 'New' },
    { value: 'VALIDATED',           label: 'Validated' },
    { value: 'SECTIONED',           label: 'Sectioned' },
    { value: 'TRIAGED',             label: 'Triaged' },
    { value: 'CRITERION_EXTRACTED', label: 'Criteria extracted' },
    { value: 'PERSONAS_EXTRACTED',  label: 'Personas extracted' },
    { value: 'SKILLS_EXTRACTED',    label: 'Skills extracted' },
    { value: 'PROCESSED',           label: 'Processed' },
    { value: 'REJECTED',            label: 'Rejected' },
  ];

  const loadTenders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTendersV2({
        page,
        pageSize: 20,
        status: statusFilter || undefined,
        sort: `${sortField},${sortDir}`,
      });
      setTenders(result.items);
      setTotalPages(result.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenders.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, sortField, sortDir, t]);

  useEffect(() => {
    loadTenders();
  }, [loadTenders]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('tenders.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('tenders.subtitle')}</p>
        </div>
        <Link to="/tenders/submit" className="btn-primary self-start sm:self-auto">
          {t('tenders.submitTender')}
        </Link>
      </header>

      {/* Filters — the v2 endpoint has no free-text search, so only the status
          filter and refresh are offered here. */}
      <section aria-label={t('tenders.filters')} className="flex flex-wrap gap-3">
        <div className="min-w-[220px]">
          <label id="status-filter-label" className="sr-only">{t('tenders.filterByStatus')}</label>
          <Select
            aria-labelledby="status-filter-label"
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_OPTIONS[0]}
            onChange={(opt) => { setStatusFilter((opt?.value ?? '') as TenderStatusV2 | ''); setPage(1); }}
            classNamePrefix="react-select"
            isSearchable={false}
            styles={{
              control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </div>
        <button onClick={loadTenders} className="btn-secondary" aria-label={t('common.refresh')}>
          {t('common.refresh')}
        </button>
      </section>

      {/* Error alert */}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">⟳</span>
          {t('tenders.loading')}
        </div>
      )}

      {/* Tender table */}
      {!loading && !error && (
        <>
          {tenders.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500 text-sm">{t('tenders.noTenders')}</p>
              <Link to="/tenders/submit" className="btn-primary mt-4 inline-flex">
                {t('tenders.submitFirst')}
              </Link>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('tenders.tenderList')}>
                  <thead>
                    <tr>
                      <SortableHeader
                        label={t('tenders.titleCol')}
                        field="title"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableHeader
                        label={t('tenders.receivedAtCol', 'Received')}
                        field="receivedAt"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th scope="col">{t('tenders.overdueCol', 'Overdue')}</th>
                      <SortableHeader
                        label={t('common.status')}
                        field="status"
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenders.map((tender) => (
                      <tr key={tender.id}>
                        <td className="font-medium text-gray-900 max-w-xs truncate">
                          <button
                            onClick={() => navigate(`/v2/tenders/${tender.id}`)}
                            className="text-left hover:text-brand-700 focus:outline-none focus-visible:underline"
                            aria-label={t('tenders.viewDetails', { title: tender.title })}
                          >
                            {tender.title}
                          </button>
                        </td>
                        <td className="text-gray-500">
                          <time dateTime={tender.received_at}>
                            {new Date(tender.received_at).toLocaleDateString(dateLocale, {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </time>
                        </td>
                        <td className="text-center">
                          {(() => {
                            const received = new Date(tender.received_at);
                            const hoursSince = (Date.now() - received.getTime()) / (1000 * 60 * 60);
                            const isOverdue = hoursSince > 48;
                            return isOverdue ? (
                              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" aria-label={t('tenders.overdueYes', 'Overdue')}>
                                {t('tenders.overdueYes', 'Overdue')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700" aria-label={t('tenders.onTime', 'On time')}>
                                {t('tenders.onTime', 'On time')}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <StatusBadge status={tender.status} />
                        </td>
                        <td>
                          <Link
                            to={`/v2/tenders/${tender.id}`}
                            className="text-xs text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                            aria-label={t('tenders.viewDetails', { title: tender.title })}
                          >
                            {t('tenders.results')}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label={t('tenders.pagination')} className="flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
                aria-label={t('common.previous')}
              >
                &larr; {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600" aria-current="page">
                {t('common.pageOf', { page, totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary"
                aria-label={t('common.next')}
              >
                {t('common.next')} &rarr;
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

// Sortable columns map to TenderV2 property names the backend accepts in the
// Spring `sort` param.
type SortField = 'title' | 'receivedAt' | 'status';
type SortDir = 'asc' | 'desc';

function SortableHeader({
  label,
  field,
  sortField,
  sortDir,
  onSort,
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th
      scope="col"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 hover:text-brand-700 focus:outline-none focus-visible:underline"
      >
        <span>{label}</span>
        <span aria-hidden="true" className={`text-xs ${active ? 'text-brand-600' : 'text-gray-300'}`}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}
